import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { jobsApi, feesApi, invoicesApi, feefoApi, feasibilityApi, photosApi, ApiError } from "@/api";
import type { JobDetailResponse } from "@/types";
import { Spinner, Button, Toast, useToast } from "@/components/ui";
import { jobStatusLabel, flagLabel, humanise, hasDerivedWorkstreamStatus, buildJobAddressString, isContainerSet } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { DeadlineChipView, WorkstreamPills, JobStagePill } from "./JobChips";
import { ApplicationsTab } from "./ApplicationsTab";
import { ClientsTab } from "./ClientsTab";
import { DrawingSetsTab } from "./DrawingSetsTab";
import { FeesTab } from "./FeesTab";
import { InvoicesTab } from "./InvoicesTab";
import { TasksTab } from "./TasksTab";
import { BuildCostTab } from "./BuildCostTab";
import { FeedbackTab } from "./FeedbackTab";
import { PhotosTab } from "./PhotosTab";
import { GetDetailedAddressDrawer } from "./GetDetailedAddressDrawer";
import "./jobs.css";

type Notice = { kind: "info" | "warn"; message: string };
type Tab = "applications" | "drawings" | "clients" | "fees" | "invoices" | "tasks" | "photos" | "build-cost" | "feedback";

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();

  const [detail, setDetail] = useState<JobDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notFound, setNotFound] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [constraintsBusy, setConstraintsBusy] = useState(false);
  const [addrDrawerOpen, setAddrDrawerOpen] = useState(false);
  // Default to Drawing sets (the primary tab, Addendum 2 §4.1), but honour an
  // initial tab passed via nav state (e.g. the Tasks dashboard deep-links to the
  // Tasks tab).
  const [tab, setTab] = useState<Tab>(
    ((location.state as { tab?: Tab } | null)?.tab) ?? "drawings",
  );
  // Fees + invoices live in the Invoicing group, not /detail, so their tab-label
  // counts are fetched separately. The tabs also report counts up after their
  // own loads so the labels stay in sync across writes.
  const [feeCount, setFeeCount] = useState<number | null>(null);
  const [invoiceCount, setInvoiceCount] = useState<number | null>(null);
  // Feefo requests live in the Feefo group, not /detail — same separate-fetch
  // pattern as fees/invoices; the tab reports its count back up after sends.
  const [feedbackCount, setFeedbackCount] = useState<number | null>(null);
  // Site photos live in their own tables (photo_set / site_photo) — same
  // separate-fetch pattern; the sets endpoint is cheap (no presigning).
  const [photoCount, setPhotoCount] = useState<number | null>(null);

  // Flash passed from the New Job drawer (or a status change) arrives via nav
  // state; surface it as a transient toast, then strip it from history so a
  // refresh doesn't resurface it.
  const { toast, showToast } = useToast();
  useEffect(() => {
    const st = location.state as { flash?: Notice; tab?: Tab } | null;
    if (st?.flash) showToast(st.flash.message, st.flash.kind);
    if (st?.flash || st?.tab) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    (opts: { initial?: boolean } = {}) => {
      let cancelled = false;
      if (opts.initial) setLoading(true);
      jobsApi
        .getJobDetail(jobId)
        .then((d) => {
          if (!cancelled) {
            setDetail(d);
            setNotFound(false);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 404) setNotFound(true);
          else setError(err instanceof ApiError ? err.message : "Could not load the job.");
        })
        .finally(() => {
          if (!cancelled && opts.initial) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    },
    [jobId],
  );

  useEffect(() => {
    if (!Number.isFinite(jobId)) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    return load({ initial: true });
  }, [jobId, load]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  // Fee + invoice counts for the tab labels (separate from /detail). Errors
  // leave the label countless rather than blocking the page.
  useEffect(() => {
    if (!Number.isFinite(jobId)) return;
    let cancelled = false;
    setFeeCount(null);
    setInvoiceCount(null);
    setFeedbackCount(null);
    setPhotoCount(null);
    feesApi
      .listFees(jobId)
      .then((res) => {
        if (!cancelled) setFeeCount(res.summary?.count ?? 0);
      })
      .catch(() => {});
    invoicesApi
      .listInvoices(jobId)
      .then((res) => {
        if (!cancelled) setInvoiceCount(res.summary?.count ?? 0);
      })
      .catch(() => {});
    feefoApi
      .listFeefoRequests(jobId)
      .then((res) => {
        if (!cancelled) setFeedbackCount(res.summary?.count ?? 0);
      })
      .catch(() => {});
    photosApi
      .listPhotoSets(jobId)
      .then((sets) => {
        if (!cancelled) setPhotoCount(sets.reduce((n, s) => n + s.photo_count, 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Stage is derived on every recompute now, so the only manual moves are the
  // two sticky stages (on_hold / archived) and resume. Resume re-derives the
  // stage server-side — never guess it here.
  async function setHeldStatus(next: "on_hold" | "archived") {
    if (!detail || next === detail.job.status || statusBusy) return;
    setStatusBusy(true);
    setError(undefined);
    try {
      const res = await jobsApi.updateJobStatus(jobId, next);
      showToast(`Status changed: ${jobStatusLabel(res.previous_status)} → ${jobStatusLabel(res.status)}`);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the status.");
    } finally {
      setStatusBusy(false);
    }
  }

  async function resume() {
    if (!detail || statusBusy) return;
    setStatusBusy(true);
    setError(undefined);
    try {
      const res = await jobsApi.resumeJob(jobId);
      showToast(`Resumed — status is now ${jobStatusLabel(res.job.status)}`);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resume the job.");
    } finally {
      setStatusBusy(false);
    }
  }

  // Look the site's statutory designations up on demand (planning.data.gov.uk via
  // the engine's POST /constraints), then persist them onto the job. This is the
  // path for jobs whose lead never carried a feasibility report — it needs a UPRN
  // or a lat/lng to key the point lookup (0,0 = unset). Bridged jobs already have
  // constraints; this refreshes them.
  async function checkConstraints() {
    if (!detail || constraintsBusy) return;
    const uprn = (detail.job.uprn ?? "").trim();
    const lat = detail.job.lat;
    const lng = detail.job.lng;
    const hasLatLng = lat != null && lng != null && !(lat === 0 && lng === 0);
    if (!uprn && !hasLatLng) {
      showToast("Set a detailed address (UPRN or lat/lng) first — use “Get detailed address”.", "warn");
      return;
    }
    setConstraintsBusy(true);
    setError(undefined);
    try {
      const report = await feasibilityApi.lookupConstraints({
        uprn: uprn || undefined,
        latitude: hasLatLng ? lat ?? undefined : undefined,
        longitude: hasLatLng ? lng ?? undefined : undefined,
      });
      await jobsApi.setJobConstraints(jobId, report);
      showToast(
        report.lookup_ok ? "Planning constraints updated." : "Lookup didn’t complete — designations unknown.",
        report.lookup_ok ? "info" : "warn",
      );
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not check planning constraints.");
    } finally {
      setConstraintsBusy(false);
    }
  }

  if (loading) {
    return (
      <section className="job-detail">
        <div className="jobs-list__loading"><Spinner label="Loading job…" /></div>
      </section>
    );
  }

  if (notFound) {
    return (
      <section className="job-detail">
        <Link to="/staff/jobs" className="job-detail__back">← All jobs</Link>
        <p className="jobs-list__empty">That job couldn’t be found.</p>
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="job-detail">
        <Link to="/staff/jobs" className="job-detail__back">← All jobs</Link>
        {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}
      </section>
    );
  }

  const { job, counts } = detail;
  // Container sets hold portal document-share files, not production drawings —
  // the Drawing sets tab and the application drawing pickers never see them.
  const productionSets = detail.drawing_sets.filter((s) => !isContainerSet(s));
  const leadClient = detail.clients.find((c) => c.is_lead) ?? null;
  const title = job.job_name || job.full_address || "Untitled job";
  // Site planning constraints — distinct from active_flags (which are aggregated
  // from parsed letters). `job.constraints` is {} until ever checked, a
  // ConstraintsReport once bridged from the lead or looked up on demand. A report
  // with lookup_ok=false means the flags are unknown, not "all clear".
  const siteConstraints =
    job.constraints && typeof job.constraints === "object" && "flags" in job.constraints
      ? job.constraints
      : null;
  const constraintFlags = siteConstraints?.flags ?? null;
  const constraintsChecked = !!siteConstraints?.checked_at;
  const activeConstraintKeys = constraintFlags
    ? Object.keys(constraintFlags).filter(
        (k) => k !== "listed_building_grade" && constraintFlags[k as keyof typeof constraintFlags] === true,
      )
    : [];
  // Drives the conservation-area warning in the New-application drawer (warn only).
  const conservationArea = !!(siteConstraints?.lookup_ok && constraintFlags?.conservation_area);
  // Whether the constraints lookup completed (a resolved answer, CA or not) — drives
  // the drawer's "constraints not checked" warning for CLOPUD / Prior Approval.
  const constraintsResolved = !!siteConstraints?.lookup_ok;
  // The header address is the single-source-of-truth job address string (prefers
  // the structured fields, postcode shown separately below; falls back to the
  // legacy full_address). "__skip__" in address_line_1 is the "deliberately not
  // enriched" sentinel — both the helper (treats it as no structured address)
  // and the lookup gate below key off it to offer the Ideal Postcodes drawer.
  const SKIP_SENTINEL = "__skip__";
  const isSkipSentinel = (job.address_line_1 ?? "").trim() === SKIP_SENTINEL;
  const headerAddress = buildJobAddressString(job);
  // Offer the lookup whenever the job has no UPRN yet (null or empty — the
  // column default is "") or its address was explicitly skipped, i.e. it hasn't
  // been resolved to a precise address.
  const hasUprn = !!(job.uprn && job.uprn.trim());
  const showAddressLookup = !hasUprn || isSkipSentinel;
  const deadlineRef = job.deadline_application_reference || job.deadline_task_title;
  const flagKeys = Object.keys(detail.active_flags ?? {});

  return (
    <section className="job-detail">
      <Link to="/staff/jobs" className="job-detail__back">← All jobs</Link>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <header className="job-card">
        <div className="job-card__main">
          <h1 className="job-card__title">
            <span className="numeric">{job.job_number}</span> — {title}
          </h1>
          <dl className="job-card__attrs">
            <div>
              <dt>Lead client</dt>
              <dd>{leadClient ? leadClient.display : <span className="job-drawer__muted">No lead client</span>}</dd>
            </div>
            <div>
              <dt>Address</dt>
              <dd className="job-card__address">
                <span>{headerAddress || "—"}</span>
                {job.postcode ? <span className="job-card__postcode">{job.postcode}</span> : null}
                {showAddressLookup ? (
                  <button type="button" className="job-card__addr-link" onClick={() => setAddrDrawerOpen(true)}>
                    Get detailed address →
                  </button>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>Next deadline</dt>
              <dd className="job-card__deadline">
                <span>{formatDateShort(job.next_deadline)}</span>
                <DeadlineChipView daysRemaining={job.days_remaining} />
                {job.deadline_type ? (
                  <span className="job-drawer__muted">
                    {humanise(job.deadline_type)}{deadlineRef ? ` · ${deadlineRef}` : ""}
                  </span>
                ) : null}
              </dd>
            </div>
          </dl>
          {job.notes ? <p className="job-card__notes">{job.notes}</p> : null}
        </div>
        <div className="job-card__status">
          <div className="job-card__overall">
            <span className="job-card__overall-label">Overall status</span>
            <JobStagePill stage={job.status} />
          </div>
          {hasDerivedWorkstreamStatus(job) ? <WorkstreamPills job={job} compact /> : null}
          <div className="job-card__stage-actions">
            {job.status !== "on_hold" && job.status !== "archived" ? (
              <Button variant="ghost" size="sm" disabled={statusBusy} onClick={() => setHeldStatus("on_hold")}>
                Put on hold
              </Button>
            ) : null}
            {job.status !== "archived" ? (
              <Button variant="ghost" size="sm" disabled={statusBusy} onClick={() => setHeldStatus("archived")}>
                Archive
              </Button>
            ) : null}
            {job.status === "on_hold" || job.status === "archived" ? (
              <Button variant="secondary" size="sm" disabled={statusBusy} onClick={resume}>
                Resume
              </Button>
            ) : null}
          </div>
        </div>

        <div className="job-constraints" aria-label="Site planning constraints">
          <span className="job-constraints__label">Site constraints</span>
          <div className="job-constraints__body">
            {constraintsChecked && activeConstraintKeys.length > 0 ? (
              <div className="job-constraints__pills">
                {activeConstraintKeys.map((key) => (
                  <span key={key} className="job-constraint">
                    {flagLabel(key)}
                    {key === "listed_building" && constraintFlags?.listed_building_grade
                      ? ` · Grade ${constraintFlags.listed_building_grade}`
                      : ""}
                  </span>
                ))}
              </div>
            ) : (
              <span className="job-constraints__muted">
                {!constraintsChecked
                  ? "Not checked yet."
                  : siteConstraints && !siteConstraints.lookup_ok
                    ? "Last lookup didn’t complete — designations unknown."
                    : "No designations found in the national dataset — to be confirmed."}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={checkConstraints} loading={constraintsBusy} disabled={constraintsBusy}>
            {constraintsChecked ? "Re-check" : "Check planning constraints"}
          </Button>
        </div>
      </header>

      {flagKeys.length > 0 ? (
        <div className="job-flags" aria-label="Active flags">
          {flagKeys.map((key) => {
            const unknown = detail.active_flags[key] === "unknown";
            return (
              <span key={key} className={`job-flag${unknown ? " job-flag--unknown" : ""}`} title={unknown ? "Unknown — not confirmed" : undefined}>
                {flagLabel(key)}{unknown ? " ?" : ""}
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="job-tabs" role="tablist" aria-label="Job sections">
        <button type="button" role="tab" aria-selected={tab === "drawings"} className={`job-tab${tab === "drawings" ? " job-tab--active" : ""}`} onClick={() => setTab("drawings")}>
          Drawing sets ({productionSets.length})
        </button>
        <button type="button" role="tab" aria-selected={tab === "applications"} className={`job-tab${tab === "applications" ? " job-tab--active" : ""}`} onClick={() => setTab("applications")}>
          Applications ({counts.applications})
        </button>
        <button type="button" role="tab" aria-selected={tab === "clients"} className={`job-tab${tab === "clients" ? " job-tab--active" : ""}`} onClick={() => setTab("clients")}>
          Clients ({counts.clients})
        </button>
        <button type="button" role="tab" aria-selected={tab === "fees"} className={`job-tab${tab === "fees" ? " job-tab--active" : ""}`} onClick={() => setTab("fees")}>
          Fees{feeCount != null ? ` (${feeCount})` : ""}
        </button>
        <button type="button" role="tab" aria-selected={tab === "invoices"} className={`job-tab${tab === "invoices" ? " job-tab--active" : ""}`} onClick={() => setTab("invoices")}>
          Invoices{invoiceCount != null ? ` (${invoiceCount})` : ""}
        </button>
        <button type="button" role="tab" aria-selected={tab === "tasks"} className={`job-tab${tab === "tasks" ? " job-tab--active" : ""}`} onClick={() => setTab("tasks")}>
          Tasks ({counts.tasks_open})
        </button>
        <button type="button" role="tab" aria-selected={tab === "photos"} className={`job-tab${tab === "photos" ? " job-tab--active" : ""}`} onClick={() => setTab("photos")}>
          Photos{photoCount != null ? ` (${photoCount})` : ""}
        </button>
        <button type="button" role="tab" aria-selected={tab === "build-cost"} className={`job-tab${tab === "build-cost" ? " job-tab--active" : ""}`} onClick={() => setTab("build-cost")}>
          Build cost
        </button>
        <button type="button" role="tab" aria-selected={tab === "feedback"} className={`job-tab${tab === "feedback" ? " job-tab--active" : ""}`} onClick={() => setTab("feedback")}>
          Feedback{feedbackCount != null ? ` (${feedbackCount})` : ""}
        </button>
      </div>

      <div className="job-tab-panel" role="tabpanel">
        {tab === "drawings" ? (
          <DrawingSetsTab
            jobId={jobId}
            jobNumber={job.job_number}
            drawingSets={productionSets}
            onChanged={reload}
            onError={setError}
            onNotice={(kind, message) => showToast(message, kind)}
            onOpenTaskBoard={() => navigate(`/staff/tasks/board?job=${jobId}`)}
          />
        ) : tab === "applications" ? (
          <ApplicationsTab
            jobId={jobId}
            jobNumber={job.job_number}
            defaultAddress={headerAddress}
            defaultPostcode={job.postcode ?? ""}
            applications={detail.applications}
            drawingSets={productionSets}
            letterSummaries={detail.letter_summaries}
            conservationArea={conservationArea}
            constraintsResolved={constraintsResolved}
            onChanged={reload}
            onError={setError}
          />
        ) : tab === "clients" ? (
          <ClientsTab
            jobId={jobId}
            clients={detail.clients}
            builders={detail.builders ?? []}
            onChanged={reload}
            onError={setError}
            onNotice={(kind, message) => showToast(message, kind)}
          />
        ) : tab === "fees" ? (
          <FeesTab
            jobId={jobId}
            applications={detail.applications}
            hasClient={detail.clients.length > 0}
            leadClientName={leadClient?.display ?? detail.clients[0]?.display ?? null}
            defaultReference={job.job_name ?? ""}
            onNotice={(kind, message) => showToast(message, kind)}
            onFeeCount={setFeeCount}
            onInvoiced={() => setTab("invoices")}
          />
        ) : tab === "invoices" ? (
          <InvoicesTab
            jobId={jobId}
            hasClient={detail.clients.length > 0}
            leadClientName={leadClient?.display ?? detail.clients[0]?.display ?? null}
            defaultReference={job.job_name ?? ""}
            onNotice={(kind, message) => showToast(message, kind)}
            onInvoiceCount={setInvoiceCount}
          />
        ) : tab === "tasks" ? (
          <TasksTab jobId={jobId} tasks={detail.tasks} applications={detail.applications} onChanged={reload} onError={setError} />
        ) : tab === "photos" ? (
          <PhotosTab
            jobId={jobId}
            jobNumber={job.job_number}
            drawingSets={productionSets}
            onError={setError}
            onNotice={(kind, message) => showToast(message, kind)}
            onCount={setPhotoCount}
          />
        ) : tab === "build-cost" ? (
          <BuildCostTab
            jobId={jobId}
            jobNumber={job.job_number}
            defaultAddress={headerAddress}
            defaultPostcode={job.postcode ?? ""}
            defaultUprn={job.uprn ?? ""}
            clientName={leadClient?.display ?? detail.clients[0]?.display ?? ""}
            onError={setError}
            onNotice={(kind, message) => showToast(message, kind)}
          />
        ) : (
          <FeedbackTab
            jobId={jobId}
            jobNumber={job.job_number}
            clientName={leadClient?.display ?? detail.clients[0]?.display ?? ""}
            clientEmail={leadClient?.email ?? detail.clients[0]?.email ?? ""}
            onError={setError}
            onNotice={(kind, message) => showToast(message, kind)}
            onCount={setFeedbackCount}
          />
        )}
      </div>

      {addrDrawerOpen ? (
        <GetDetailedAddressDrawer
          jobId={jobId}
          fullAddress={job.full_address ?? ""}
          postcode={job.postcode ?? ""}
          onClose={() => setAddrDrawerOpen(false)}
          onSaved={() => {
            setAddrDrawerOpen(false);
            showToast("Address updated.");
            reload();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
